import { useEffect, useMemo, useState } from "react";
import { Drawer, message } from "antd";
import { Link } from "react-router-dom";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { careService } from "../../services/careService";

const firstText = (...values) =>
  values.map((value) => String(value || "").trim()).find(Boolean) || "";

const formatDateTime = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const statusLabel = (value) => firstText(value, "Open").replace(/_/g, " ");

export const OCARE_MODULES = {
  compliance: {
    title: "Compliance Dashboard",
    eyebrow: "WAC 388-76 and AFH compliance",
    description: "Track compliance alerts, open documentation gaps, incident follow-up, and inspection-ready bundles.",
    icon: "ti-shield-check",
    tone: "blue",
    serviceKey: "compliance",
    createLabel: "Add compliance item",
    emptyText: "No compliance items have been added yet.",
    fields: [
      ["title", "Title", "text", "Medication record review"],
      ["category", "Category", "select", "", ["WAC 388-76", "HIPAA", "Resident rights", "Medication", "Staffing", "Documents", "Other"]],
      ["severity", "Severity", "select", "Medium", ["Low", "Medium", "High", "Critical"]],
      ["dueDate", "Due date", "date"],
      ["description", "Description", "textarea", "What needs to be reviewed, corrected, or bundled?"],
    ],
  },
  complianceInventory: {
    title: "Compliance Inventory",
    eyebrow: "Compliance documents and readiness",
    description: "Track compliance documents, review cycles, owners, due dates, and readiness status.",
    icon: "ti-clipboard-list",
    tone: "blue",
    serviceKey: "complianceInventory",
    createLabel: "Add inventory item",
    emptyText: "No compliance inventory items have been added yet.",
    fields: [
      ["title", "Item name", "text", "Emergency evacuation plan"],
      ["category", "Category", "select", "", ["Licensing", "Resident records", "Staff records", "Drills", "Policies", "Training", "Other"]],
      ["owner", "Owner", "text"],
      ["reviewDate", "Next review date", "date"],
      ["status", "Status", "select", "In review", ["Current", "In review", "Needs update", "Expired"]],
      ["description", "Details", "textarea", "Document location, requirements, gaps, and next actions."],
    ],
  },
  complianceAlerts: {
    title: "Compliance Alerts",
    eyebrow: "Open compliance risks and deadlines",
    description: "Monitor compliance deadlines, overdue requirements, risk flags, and follow-up actions.",
    icon: "ti-alert-triangle",
    tone: "amber",
    serviceKey: "complianceAlerts",
    createLabel: "Add alert",
    emptyText: "No compliance alerts have been added yet.",
    fields: [
      ["title", "Alert title", "text", "Staff credential expiring"],
      ["category", "Category", "select", "", ["Deadline", "Missing document", "Expired item", "Inspection", "Training", "Other"]],
      ["dueDate", "Due date", "date"],
      ["severity", "Severity", "select", "Medium", ["Low", "Medium", "High", "Critical"]],
      ["status", "Status", "select", "Open", ["Open", "In review", "Resolved", "Dismissed"]],
      ["description", "Details", "textarea", "Describe the risk, requirement, and follow-up needed."],
    ],
  },
  incidents: {
    title: "Incident Reporting",
    eyebrow: "Resident safety and follow-up",
    description: "Capture incident reports, immediate actions, notifications, documents, and resolution status.",
    icon: "ti-alert-triangle",
    tone: "amber",
    serviceKey: "incidents",
    createLabel: "Report incident",
    emptyText: "No incidents have been reported yet.",
    fields: [
      ["title", "Incident title", "text", "Fall without injury"],
      ["category", "Incident type", "select", "", ["Fall", "Medication", "Behavior", "Injury", "Elopement", "Property", "Other"]],
      ["occurredAt", "Occurred at", "datetime-local"],
      ["severity", "Severity", "select", "Medium", ["Low", "Medium", "High", "Critical"]],
      ["description", "What happened", "textarea", "Describe facts, actions taken, and who was notified."],
    ],
  },
  cbhs: {
    title: "CBHS Supportive Supervision",
    eyebrow: "Supervision and monthly reporting",
    description: "Track CBHS supportive supervision contacts, progress notes, monthly summaries, and report readiness.",
    icon: "ti-user-shield",
    tone: "purple",
    serviceKey: "cbhs",
    createLabel: "Add supervision note",
    emptyText: "No CBHS supervision records have been added yet.",
    fields: [
      ["title", "Support area", "text", "Community access supervision"],
      ["category", "Category", "select", "", ["Supportive supervision", "Behavioral support", "Community integration", "Monthly summary", "Other"]],
      ["occurredAt", "Contact date/time", "datetime-local"],
      ["progress", "Progress", "select", "Ongoing", ["Improved", "Ongoing", "Needs follow-up", "Escalated"]],
      ["description", "Details", "textarea", "Document observations, interventions, and monthly report notes."],
    ],
  },
  rnDelegation: {
    title: "RN Delegation",
    eyebrow: "Delegated nursing tasks",
    description: "Track RN delegation workflows, delegated tasks, review dates, caregiver training, and signature readiness.",
    icon: "ti-stethoscope",
    tone: "blue",
    serviceKey: "rnDelegations",
    createLabel: "Add delegation",
    emptyText: "No RN delegations have been added yet.",
    fields: [
      ["title", "Delegated task", "text", "Blood glucose check"],
      ["residentName", "Resident", "text"],
      ["delegatingRn", "Delegating RN", "text"],
      ["reviewDate", "Review date", "date"],
      ["status", "Status", "select", "Draft", ["Draft", "Active", "Needs signature", "Expired"]],
      ["description", "Instructions", "textarea", "Delegation instructions, training notes, and follow-up requirements."],
    ],
  },
  familyPortal: {
    title: "Family Portal",
    eyebrow: "Family updates and signatures",
    description: "Manage family portal access, shared updates, signature requests, and representative communication.",
    icon: "ti-users-group",
    tone: "amber",
    serviceKey: "familyPortal",
    createLabel: "Add portal item",
    emptyText: "No family portal items have been added yet.",
    fields: [
      ["title", "Title", "text", "NCP signature request"],
      ["recipient", "Family/representative", "text"],
      ["category", "Type", "select", "", ["Update", "Document share", "Signature request", "Message", "Other"]],
      ["dueDate", "Due date", "date"],
      ["description", "Message", "textarea", "What should the family member review or sign?"],
    ],
  },
  secureExchange: {
    title: "Secure Care-Team Exchange",
    eyebrow: "Fax, email, and document exchange",
    description: "Track secure fax/email exchanges with pharmacies, RNs, PCPs, case managers, and care-team partners.",
    icon: "ti-mail-lock",
    tone: "blue",
    serviceKey: "secureExchange",
    createLabel: "Log exchange",
    emptyText: "No secure exchanges have been logged yet.",
    fields: [
      ["title", "Subject", "text", "Medication clarification"],
      ["recipient", "Recipient", "text"],
      ["channel", "Channel", "select", "", ["Secure email", "Secure fax", "Document exchange", "Other"]],
      ["sentAt", "Sent/received at", "datetime-local"],
      ["description", "Details", "textarea", "Document what was sent, received, or requested."],
    ],
  },
  carePlanning: {
    title: "Meal and Activity Planning",
    eyebrow: "Daily life and ADL planning",
    description: "Plan meals, activities, ADL routines, resident preferences, and automatic task follow-up.",
    icon: "ti-tools-kitchen-2",
    tone: "amber",
    serviceKey: "carePlanning",
    createLabel: "Add plan item",
    emptyText: "No meal, activity, or ADL plan items have been added yet.",
    fields: [
      ["title", "Plan item", "text", "Hydration encouragement"],
      ["category", "Category", "select", "", ["ADL", "Meal", "Hydration", "Activity", "Preference", "Task", "Other"]],
      ["scheduledFor", "Scheduled for", "datetime-local"],
      ["frequency", "Frequency", "select", "", ["One time", "Daily", "Weekly", "Monthly", "As needed"]],
      ["description", "Details", "textarea", "Resident preferences, staff instructions, and task notes."],
    ],
  },
  hoursOperations: {
    title: "Hours and Timesheet Operations",
    eyebrow: "Hours, corrections, and disputes",
    description: "Review regular time, overtime, weekly/monthly caregiver hours, correction requests, and shift disputes.",
    icon: "ti-clock-dollar",
    tone: "purple",
    serviceKey: "hoursOperations",
    createLabel: "Add hours item",
    emptyText: "No hours operation items have been added yet.",
    fields: [
      ["title", "Title", "text", "Timesheet correction request"],
      ["caregiverName", "Caregiver", "text"],
      ["category", "Category", "select", "", ["Regular time", "Overtime", "Weekly hours", "Monthly hours", "Correction", "Dispute"]],
      ["workDate", "Work date", "date"],
      ["status", "Status", "select", "Open", ["Open", "In review", "Approved", "Denied", "Resolved"]],
      ["description", "Details", "textarea", "Correction reason, dispute notes, or report details."],
    ],
  },
};

const valueFor = (record = {}, key) =>
  firstText(record[key], record.payload?.[key], record.details?.[key]);

function OCareFeaturePage({ moduleKey }) {
  const module = OCARE_MODULES[moduleKey] || OCARE_MODULES.incidents;
  const listOnly = moduleKey === "complianceAlerts";
  const { user } = useAuth();
  const { selectedWorkspaceId, selectedWorkspace } = useWorkspace();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState({});

  const actorId = firstText(user?.id, user?.userId, user?.uid);
  const actorName = firstText(
    user?.name,
    user?.fullName,
    user?.displayName,
    [user?.firstName, user?.middleName, user?.lastName].filter(Boolean).join(" "),
    user?.email,
  );
  const workspaceName = firstText(selectedWorkspace?.organization?.name, selectedWorkspace?.organizationName, selectedWorkspace?.name, "Current workspace");
  const api = careService[module.serviceKey];

  const visibleRecords = useMemo(
    () => [...records].sort((a, b) => new Date(valueFor(b, "updatedAt") || valueFor(b, "createdAt") || 0) - new Date(valueFor(a, "updatedAt") || valueFor(a, "createdAt") || 0)),
    [records],
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    if (!api?.list) {
      setRecords([]);
      setLoading(false);
      return () => {
        active = false;
      };
    }
    api.list({ workspaceId: selectedWorkspaceId, wid: selectedWorkspaceId })
      .then((payload) => {
        if (!active) return;
        const items = Array.isArray(payload) ? payload : payload?.content || payload?.data || payload?.items || [];
        setRecords(items);
      })
      .catch((err) => {
        if (!active) return;
        setRecords([]);
        setError(err?.response?.status === 404 ? "" : (err?.response?.data?.message || err?.message || `Unable to load ${module.title.toLowerCase()}.`));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [api, module.title, selectedWorkspaceId]);

  const updateField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const openCreate = () => {
    setForm(Object.fromEntries(module.fields.map(([key, , , defaultValue]) => [key, defaultValue || ""])));
    setDrawerOpen(true);
  };

  const saveRecord = async (event) => {
    event.preventDefault();
    if (!api?.create) {
      message.error(`${module.title} is not connected to the care service yet.`);
      return;
    }
    setSaving(true);
    setError("");
    const now = new Date().toISOString();
    const payload = {
      ...form,
      workspaceId: selectedWorkspaceId,
      wid: selectedWorkspaceId,
      createdById: actorId,
      createdByName: actorName,
      reportedById: actorId,
      reportedByName: actorName,
      status: form.status || "Open",
      createdAt: now,
      updatedAt: now,
    };
    try {
      const saved = await api.create(payload, { actorId, workspaceId: selectedWorkspaceId, wid: selectedWorkspaceId });
      setRecords((current) => [saved || payload, ...current]);
      setDrawerOpen(false);
      message.success(`${module.title} item saved.`);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || `Unable to save ${module.title.toLowerCase()}.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <CareShell className="dashboard-content app-page-content">
      <section className="care-page-hero">
        <div>
          <span>{module.eyebrow}</span>
          <h1>{module.title}</h1>
          <p>{module.description}</p>
        </div>
        {!listOnly ? (
          <button className="mini-btn primary" type="button" onClick={openCreate}>
            <i className="ti ti-plus" /> {module.createLabel}
          </button>
        ) : null}
      </section>

      {error ? <p className="form-error">{error}</p> : null}

      {!listOnly ? <div className="stats-grid">
        <Panel title="Workspace">
          <div className="list-row">
            <span className={`round-icon ${module.tone}`}><i className={`ti ${module.icon}`} /></span>
            <div><strong>{workspaceName}</strong><small>{module.eyebrow}</small></div>
            <em>Active</em>
          </div>
        </Panel>
        <Panel title="Records">
          <div className="list-row">
            <span className="round-icon blue"><i className="ti ti-list-check" /></span>
            <div><strong>{visibleRecords.length}</strong><small>Total records in this module</small></div>
            <em>{loading ? "Loading" : "Ready"}</em>
          </div>
        </Panel>
      </div> : null}

      <Panel title={module.title}>
        <div className="resident-tab-list" style={{ "--resident-record-columns": "minmax(220px, 1fr) 145px 130px minmax(220px, 1.2fr) 140px 28px" }}>
          <div className="resident-tab-list-row resident-tab-list-head-row">
            <span>Title</span>
            <span>Category</span>
            <span>Status</span>
            <span>Details</span>
            <span>Updated</span>
            <span />
          </div>
          {visibleRecords.map((record, index) => (
            <article className="resident-tab-list-row" key={record.id || `${moduleKey}-${index}`}>
              <span><strong>{valueFor(record, "title") || valueFor(record, "name") || module.title}</strong></span>
              <span>{valueFor(record, "category") || valueFor(record, "channel") || valueFor(record, "type") || "General"}</span>
              <span><em className="pill blue">{statusLabel(valueFor(record, "status"))}</em></span>
              <span>{valueFor(record, "description") || valueFor(record, "summary") || "No details added."}</span>
              <span>{formatDateTime(valueFor(record, "updatedAt") || valueFor(record, "createdAt"))}</span>
              <i className="ti ti-chevron-right" />
            </article>
          ))}
          {loading ? <div className="resident-inline-empty">Loading records...</div> : null}
          {!loading && !visibleRecords.length ? <div className="resident-inline-empty">{module.emptyText}</div> : null}
        </div>
      </Panel>

      {!listOnly ? <Panel title="Related OCare Workflows">
        <div className="action-grid">
          <Link className="action-card" to="/residents">
            <span className="round-icon blue"><i className="ti ti-users" /></span>
            <div><h2>Residents</h2><p>Open resident records, documents, assessments, and daily notes.</p></div>
            <i className="ti ti-arrow-right arrow" />
          </Link>
          <Link className="action-card" to="/resident-medications">
            <span className="round-icon amber"><i className="ti ti-pill" /></span>
            <div><h2>eMAR</h2><p>Review medication orders, administration windows, and pharmacy workflows.</p></div>
            <i className="ti ti-arrow-right arrow" />
          </Link>
          <Link className="action-card" to="/timesheets">
            <span className="round-icon purple"><i className="ti ti-clock-dollar" /></span>
            <div><h2>Hours</h2><p>Review caregiver hours, timesheets, corrections, and overtime.</p></div>
            <i className="ti ti-arrow-right arrow" />
          </Link>
        </div>
      </Panel> : null}

      {!listOnly ? <Drawer
        title={module.createLabel}
        placement="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={560}
        className="resident-action-drawer"
      >
        <form className="assessment-drawer-form" onSubmit={saveRecord}>
          <section className="assessment-drawer-section">
            <h3>{module.title}</h3>
            <div className="assessment-drawer-grid">
              {module.fields.map(([key, label, type, placeholder, options]) => (
                <label className={`ncp-form-field${type === "textarea" ? " wide" : ""}`} key={key}>
                  <span>{label}</span>
                  {type === "select" ? (
                    <select value={form[key] || ""} onChange={(event) => updateField(key, event.target.value)}>
                      <option value="">Select</option>
                      {(options || []).map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                  ) : type === "textarea" ? (
                    <textarea value={form[key] || ""} onChange={(event) => updateField(key, event.target.value)} placeholder={placeholder || ""} rows={4} />
                  ) : (
                    <input type={type || "text"} value={form[key] || ""} onChange={(event) => updateField(key, event.target.value)} placeholder={placeholder || ""} />
                  )}
                </label>
              ))}
            </div>
          </section>
          <div className="assessment-drawer-actions">
            <button className="btn secondary" type="button" onClick={() => setDrawerOpen(false)} disabled={saving}>Cancel</button>
            <button className="btn primary" type="submit" disabled={saving}>
              {saving ? <><i className="ti ti-loader-2" /> Saving...</> : "Save"}
            </button>
          </div>
        </form>
      </Drawer> : null}
    </CareShell>
  );
}

export default OCareFeaturePage;
